/**
 * DBS-359: browser page translation (Chrome's "Translate page", also on iPhone) replaces the text React rendered
 * with its own <font> elements. React still holds the old text nodes, so a later update that removes one, or inserts
 * next to one, throws NotFoundError and the applicant gets the error page (React issue 11538).
 *
 * Applicants may need the translation, so rather than turning it off we let those two DOM calls skip a node that is
 * no longer where React expects it. The page keeps working; at worst some translated text is left as it was.
 */
type GuardedNode = { parentNode: unknown }
type NodePrototype = {
  removeChild: (this: unknown, child: GuardedNode) => unknown,
  insertBefore: (this: unknown, node: GuardedNode, reference: GuardedNode | null) => unknown,
}

export const guardAgainstPageTranslation = (proto: NodePrototype, warn: (message: string) => void = console.warn): void => {
  const removeChild = proto.removeChild
  proto.removeChild = function (this: unknown, child: GuardedNode) {
    if (child.parentNode !== this) {
      warn('[translationGuard] removeChild skipped: the node was moved, probably by page translation')
      return child
    }
    return removeChild.call(this, child)
  }

  const insertBefore = proto.insertBefore
  proto.insertBefore = function (this: unknown, node: GuardedNode, reference: GuardedNode | null) {
    if (reference && reference.parentNode !== this) {
      warn('[translationGuard] insertBefore skipped: the reference node was moved, probably by page translation')
      return node
    }
    return insertBefore.call(this, node, reference)
  }
}
